import React, { useState, useCallback, useContext, useEffect } from 'react';
import { ThemeProvider } from 'styled-components';
import { TabsProps, TypesIconsType } from './types';
import {
  ContentSection,
  ContentWrapper,
  RenderTab,
  TabsContainer,
  TabsSection,
  TabsWrapper,
} from './styles';
import { ThemeContext } from '../ThemeProvider/ThemeProvider';
import ComplexIcon from '../ComplexIcon/ComplexIcon';
import BasicIcon from '../BasicIcon/BasicIcon';
import { BasicIconsType, ComplexIconsType } from '..';
import { SizesType } from '../ThemeProvider/types';

const Tabs: React.FC<TabsProps> = (props) => {
  const {
    initialIndex = 0,
    children,
    height,
    onChange,
    tabs,
    fullWidth,
    typeIcons = 'complex',
    size = 'medium',
    align = 'left',
    adjustableWidth = false,
    width,
  } = props;
  const [index, setIndex] = useState(initialIndex);

  const handleTabChange = useCallback(
    (newIndex: number, disabled: boolean) => () => {
      if (!disabled) {
        if (onChange) onChange(() => setIndex(newIndex), newIndex);
        else setIndex(newIndex);
      }
    },
    [onChange],
  );

  useEffect(() => {
    setIndex(initialIndex);
  }, [initialIndex]);

  const { actualTheme } = useContext(ThemeContext);

  const positions = React.Children.map(children, (child) => child);
  const content = React.Children.map(positions[0], (child) => {
    if (React.isValidElement(child)) {
      return child.props.children;
    }
    return child;
  });

  const renderBasicIcon = useCallback(
    (tab, i) => (
      <BasicIcon
        name={tab.icon as BasicIconsType}
        clickable
        disabled={tab.disabled}
        size={`tab-size-${size}` as SizesType}
        color={index === i ? actualTheme.tabs.active : actualTheme.gray4D}
      />
    ),
    [actualTheme, index, size],
  );

  const renderComplexIcon = useCallback(
    (tab, i) => (
      <ComplexIcon
        name={tab.icon as ComplexIconsType}
        clickable
        disabled={tab.disabled}
        size={`tab-size-${size}` as SizesType}
        color={index === i ? actualTheme.tabs.active : actualTheme.gray4D}
      />
    ),
    [actualTheme, index, size],
  );

  const renderIcon: { [key in TypesIconsType]: Function } = {
    basic: renderBasicIcon,
    complex: renderComplexIcon,
  };

  return (
    <ThemeProvider theme={actualTheme}>
      <TabsWrapper height={height || 'auto'}>
        <TabsContainer>
          <TabsSection>
            {(tabs as any[]).map((tab, i) => (
              <>
                <RenderTab
                  disabled={tab.disabled}
                  onClick={handleTabChange(i, tab.disabled)}
                  active={index === i}
                  index={i}
                  length={tabs.length}
                  fullWidth={fullWidth}
                  width={width}
                  size={size}
                  align={align}
                  adjustableWidth={adjustableWidth}
                  data-testid={tab.testId}
                >
                  {size !== 'small' &&
                    size !== 'minimal' &&
                    tab.icon &&
                    renderIcon[typeIcons](tab, i)}
                  {tab.title}
                </RenderTab>
              </>
            ))}
          </TabsSection>
          <ContentSection>
            {content.map((cont, i) => (
              <div>
                {i === index && (
                  <ContentWrapper data-testid="tab-content-test">{cont}</ContentWrapper>
                )}
              </div>
            ))}
          </ContentSection>
        </TabsContainer>
      </TabsWrapper>
    </ThemeProvider>
  );
};

export default Tabs;
